import * as React from 'react';
import { Card, Table, Spin, Icon } from 'antd';
import moment from 'moment';
import Chart from '../../common/chart/Chart';
import { fetchReportChart } from '../../../api/reporting';

const loadingIndicator = <Icon type="loading" style={{ fontSize: 36 }} spin />;

interface ReportChartCardProps {
  reportKey: string;
  chart: { key: string; title: string; visualisationType: string; width: number };
  filters: object;
}

// Strict ISO-8601 datetime so we never touch reference numbers / plain strings.
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
const NUMERIC = /^-?\d+(\.\d+)?$/;

const isMoneyColumn = (name: string) => name.indexOf('$') !== -1;

const formatCell = (value: any, columnName: string) => {
  if (value === null || value === undefined || value === '') {
    return value;
  }
  if (typeof value === 'string' && ISO_DATETIME.test(value)) {
    return moment(value).format('DD MMM YYYY, h:mm A');
  }
  const isNumeric = typeof value === 'number' || (typeof value === 'string' && NUMERIC.test(value));
  if (isNumeric && isMoneyColumn(columnName)) {
    return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return value;
};

const ReportChartCard = ({ reportKey, chart, filters }: ReportChartCardProps) => {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<any>(null);
  const [error, setError] = React.useState<string>('');
  const [visible, setVisible] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Lazy load: only fetch a chart's data once its card scrolls near the viewport,
  // so opening the report doesn't fire all charts' heavy queries at once.
  React.useEffect(() => {
    if (visible) {
      return undefined;
    }
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '250px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  React.useEffect(() => {
    if (!visible) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchReportChart(reportKey, chart.key, filters)
      .then((res: any) => {
        if (cancelled) {
          return;
        }
        if (res.isSuccess) {
          setData(res.data);
        } else {
          setError('Could not load this chart');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [visible, reportKey, chart.key, JSON.stringify(filters)]);

  const renderBody = () => {
    if (!visible || loading) {
      return (
        <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin indicator={loadingIndicator} tip="Loading…" />
        </div>
      );
    }
    if (error) {
      return <div style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{error}</div>;
    }
    if (!data || !data.rows || !data.rows.length) {
      return <div style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No data</div>;
    }
    if (chart.visualisationType === 'table') {
      return (
        <Table
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, size: 'small', showSizeChanger: false }}
          columns={data.columns.map((c: any) => ({
            title: c.name,
            dataIndex: c.id,
            key: c.id,
            ellipsis: true,
            render: (value: any) => formatCell(value, c.name),
          }))}
          dataSource={data.rows.map((row: any[], i: number) =>
            data.columns.reduce((acc: any, c: any, idx: number) => ({ ...acc, [c.id]: row[idx] }), { key: i })
          )}
        />
      );
    }
    return <Chart visType={chart.visualisationType} formData={{}} chartData={data.rows} width="100%" height={320} />;
  };

  return (
    <div ref={containerRef}>
      <Card title={chart.title} style={{ marginBottom: 16 }}>
        {renderBody()}
      </Card>
    </div>
  );
};

export default ReportChartCard;
